import { DateTime } from "luxon";
import { type FC, useEffect, useEffectEvent, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useUserSettingsContext } from "../../contexts/Staffing/UserSettingsContext";
import { useWeekIndex } from "../../contexts/WeekIndexProvider";
import { SynergyModeStaffing } from "../../pages/SynergyModeStaffing/SynergyModeStaffing";
import { WeeklyViewOption } from "../../utils/models";
import { type AppliedRouteWeek, rememberedRouteWeek, resolveAppliedRouteWeek } from "./appliedRouteWeek";
import { writeLastStaffingView } from "./MonthlyView/jobView/monthlyViewPreferences";
import Staffing from "./Staffing";
import Staffingv2 from "./Staffingv2";

/**
 * The staffing grid for this department type. The other views of the Staffing area (Day, Month, and the
 * People roster) are their own routes, so this only has to pick a grid.
 */
const StaffingAreaContent: FC = () => {
	const { settings } = useUserSettingsContext();
	const { isMedicalDepartment } = useHospitalData();

	// The department-type dispatch: synergy mode first (before settings.view), then the v2/v1 grids.
	if (isMedicalDepartment) return <SynergyModeStaffing />;
	if (settings.view === WeeklyViewOption.V2) return <Staffingv2 />;
	return <Staffing />;
};

export const StaffingWrapper: FC = () => {
	const { dateString } = useParams<{ dateString?: string }>();
	const { hospitalData, getWeekIndexByDate, timezone, chosenDepartment } = useHospitalData();
	const { weekIndex, setWeekIndex } = useWeekIndex();
	const { track } = useAnalytics();

	const trackPageLoad = useEffectEvent(() => {
		track("staffing-page-load");
	});
	useEffect(() => {
		trackPageLoad();
		// Remember that the scheduler last used the weekly grid, so the Monthly view (APP-465) can honor
		// "remember the last view" on a future landing. NOTE: the PRD's landing-redirect that defaults new
		// users to Monthly is intentionally deferred — it belongs at the Staffing nav entry point, not
		// here, so switching from Monthly back to Weekly can't bounce the user straight back to Monthly.
		writeLastStaffingView("weekly");
	}, []);

	// The route date, applied once it can be resolved in the loaded weeks. Week arrows move the
	// visible week with `replaceState`, which does not change `dateString`, so a later weeks refresh
	// must not pull the grid back to the route. Accept on the census notice is one such refresh: it
	// rebuilds the weeks array, `getWeekIndexByDate` changes, and re-applying the route date jumped
	// the schedule to another week instead of leaving the open menu's week in place.
	const appliedRouteWeekRef = useRef<AppliedRouteWeek | null>(null);
	useEffect(() => {
		const remembered = rememberedRouteWeek(dateString, appliedRouteWeekRef.current);
		if (!dateString) {
			appliedRouteWeekRef.current = remembered;
			return;
		}
		// Parsed IN the department's zone, not converted into it afterwards. `fromISO` reads a bare
		// `yyyy-MM-dd` at the BROWSER's midnight, so `.setZone(timezone)` then walks it backwards for any
		// browser ahead of the department — far enough to land on the previous day, and so on the previous
		// week whenever the date is a week start. That only became reachable once a department switch
		// started preserving the date it was looking at.
		const date = DateTime.fromISO(dateString, { zone: timezone });
		const next = resolveAppliedRouteWeek(remembered, {
			dateString,
			departmentId: chosenDepartment?.department_id ?? "",
			weekIndex,
			calculatedWeekIndex: getWeekIndexByDate(date),
		});
		if (!next) return;

		appliedRouteWeekRef.current = next;
		if (weekIndex !== next.appliedIndex) setWeekIndex(next.appliedIndex);
	}, [dateString, getWeekIndexByDate, timezone, setWeekIndex, chosenDepartment?.department_id, weekIndex]);

	// Which week to open on when the URL doesn't name a date -- signing in, or landing on the bare
	// /staffing route. `weekIndex` starts at 0, and week 0 is NOT the current week: the server returns
	// `department_data_history_weeks` of history before the date it was asked for, so week 0 sits weeks
	// in the past. That is why login landed on a five-week-old date in a department the user had never
	// chosen. Snapped once per department rather than on every render of the effect, because the URL
	// write below uses `replaceState` -- which doesn't refresh `useParams` -- so `dateString` stays
	// undefined afterwards and a standing rule here would fight the week arrows.
	const snappedDepartmentRef = useRef<string | null>(null);
	useEffect(() => {
		const departmentId = chosenDepartment?.department_id;
		if (dateString || !departmentId || hospitalData.weeks.length === 0) return;
		if (snappedDepartmentRef.current === departmentId) return;

		// Claim the one-shot only once the snap actually lands. A non-empty `weeks` array does not mean the
		// week containing today has hydrated -- `getWeekIndexByDate` guards against a partially-built week
		// and returns -1 -- so marking the department first would spend the single attempt on a pass that
		// did nothing, and the grid would sit on week 0 (history) for the rest of the session.
		const todaysWeekIndex = getWeekIndexByDate(DateTime.now().setZone(timezone));
		if (todaysWeekIndex === -1) return;

		snappedDepartmentRef.current = departmentId;
		setWeekIndex(todaysWeekIndex);
	}, [dateString, chosenDepartment?.department_id, hospitalData.weeks.length, getWeekIndexByDate, timezone, setWeekIndex]);

	// Update URL with current week's start date if no dateString in URL
	useEffect(() => {
		if (!dateString && hospitalData.weeks.length > 0 && chosenDepartment?.department_id) {
			const weekStartDate = hospitalData.weeks[weekIndex]?.weeklyShifts[0]?.days[0]?.date;
			if (weekStartDate) {
				const formattedDate = weekStartDate.toFormat("yyyy-MM-dd");
				const currentPath = window.location.pathname;
				const expectedPath = `/${chosenDepartment.department_id}/staffing/${formattedDate}`;
				// Only update if the current path doesn't match
				if (currentPath !== expectedPath) {
					window.history.replaceState(null, "", expectedPath);
				}
			}
		}
	}, [dateString, weekIndex, hospitalData.weeks, chosenDepartment]);

	return <StaffingAreaContent />;
};
